<template>
  <button
    class="ld-tag"
    :class="[
      typeof size === 'string' ? `ld-tag--size-${size}` : 'ld-tag--size-custom',
      { 'has-icon': !!icon },
      { 'has-label': !!label },
      { loading },
    ]"
    :disabled="disabled || loading"
    @click.stop="handleClick"
  >
    <Icon
      v-if="icon"
      :name="icon"
      class="ld-tag-icon"
    />
    {{ label }}
  </button>
</template>

<script lang="ts">
import type { IconName } from '@/types/generated/icons.gen'

export const TAG_SIZES = ['large', 'medium'] as const
export type TagSize = (typeof TAG_SIZES)[number]

export interface TagProps {
  /** 标签文本 */
  label?: string
  /** 是否禁用 */
  disabled?: boolean
  /** 标签大小，支持预设大小 ('large', 'medium') 或具体像素值 (数字) */
  size?: number | TagSize
  /** 标签图标 */
  icon?: IconName
  /** 是否处于加载状态 */
  loading?: boolean
}
</script>

<script setup lang="ts">
import { computed } from 'vue'
import Icon from '@/components/basic/trivial/Icon.vue'
import { useDebounceClick } from '@/composables/debounceClick'

const props = withDefaults(defineProps<TagProps>(), {
  disabled: false,
  label: '',
  size: 'large' as TagSize, // withDefaults 对复杂类型支持不好，实际使用中不需要额外类型断言
  icon: undefined,
  loading: false,
})

const emit = defineEmits<{
  click: [event: MouseEvent]
}>()

// 计算自定义尺寸值
const customSize = computed(() => {
  return typeof props.size === 'number' ? props.size : null
})

const customScale = computed(() => {
  return customSize.value ? customSize.value / 28 : null // 以28px(large)为基准
})

// 使用防抖composable
const { handleClick } = useDebounceClick((event: MouseEvent) => {
  emit('click', event)
})
</script>

<style scoped>
.ld-tag {
  position: relative;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  border-radius: 9999px;

  /* 基础样式使用变量 */
  height: var(--ld-tag--height);
  min-width: var(--ld-tag--min-width);
  padding: var(--ld-tag--padding);
  gap: var(--ld-tag--gap);
  font: var(--ld2-iconlabel-large);
  font-size: var(--ld-tag--font-size);

  /* 颜色 */
  background-color: rgba(21, 21, 21, 0.3);
  color: #fff;
}

.ld-tag-icon {
  fill: currentColor;
  width: auto;
  height: var(--ld-tag--icon-size);
  min-width: var(--ld-tag--icon-size);
}

/* 尺寸变体 - 只定义变量值 */
.ld-tag--size-large {
  --ld-tag--height: 28px;
  --ld-tag--min-width: 28px;
  --ld-tag--padding: var(--ld2-spacing-1-5);
  --ld-tag--gap: var(--ld2-spacing-1);
  --ld-tag--font-size: 16px;
  --ld-tag--icon-size: 16px;
}

.ld-tag--size-large.has-label {
  --ld-tag--padding: var(--ld2-spacing-1-5) var(--ld2-spacing-2);
}

.ld-tag--size-medium {
  --ld-tag--height: 20px;
  --ld-tag--min-width: 20px;
  --ld-tag--padding: var(--ld2-spacing-1);
  --ld-tag--gap: var(--ld2-spacing-0-5);
  --ld-tag--font-size: 11px;
  --ld-tag--icon-size: 12px;
}

.ld-tag--size-medium.has-label {
  --ld-tag--padding: var(--ld2-spacing-1) var(--ld2-spacing-1-5);
}

.ld-tag--size-custom {
  --ld-tag--height: calc(28px * v-bind(customScale));
  --ld-tag--min-width: calc(28px * v-bind(customScale));
  --ld-tag--padding: calc(var(--ld2-spacing-1-5) * v-bind(customScale));
  --ld-tag--gap: calc(var(--ld2-spacing-1) * v-bind(customScale));
  --ld-tag--font-size: calc(16px * v-bind(customScale));
  --ld-tag--icon-size: calc(16px * v-bind(customScale));
}

.ld-tag--size-custom.has-label {
  --ld-tag--padding: calc(var(--ld2-spacing-1-5) * v-bind(customScale))
    calc(var(--ld2-spacing-2) * v-bind(customScale));
}
</style>
